import { forwardRef, HtmlHTMLAttributes, memo, ReactNode } from "react";
import classNames from "classnames";
import { RefType } from "../../types";

import styles from "./styles.module.scss";

export type BadgeSize = "sm" | "md" | "lg";
export type BadgeType = "pillColor" | "pillOutline" | "badgeColor" | "badgeModern";
export type BadgeColor = "gray" | "brand" | "error" | "warning" | "success" | "blueLight" | "blue" | "indigo" | "purple" | "pink" | "orange" | "grayBlue";

export type BadgeProps = HtmlHTMLAttributes<HTMLDivElement> & {
  ref?: RefType<HTMLDivElement>
  badgeSize?: BadgeSize,
  badgeType?: BadgeType,
  badgeColor?: BadgeColor
} & (
  {
    icon?: never,
    iconPosition?: never,
    iconBlockProps?: never,
    iconOnly?: never
  } |
  {
    icon: ReactNode,
    iconPosition?: "start" | "end",
    iconBlockProps?: HtmlHTMLAttributes<HTMLDivElement>,
    iconOnly?: boolean
  })

export const Badge = memo<BadgeProps>(forwardRef((
  {
    className,
    children,
    badgeSize = "md",
    badgeType = "badgeColor",
    badgeColor = "brand",
    iconPosition = "start",
    iconBlockProps,
    iconOnly = false,
    icon,
    ...props
  },
  ref,
) => {
  const iconBlock = (!!icon || iconOnly) && (
    <div {...iconBlockProps} className={classNames(iconBlockProps?.className, styles.icon)}>
      {icon}
    </div>
  );

  return (
    <div
      className={classNames(
        className,
        styles.root,
        styles[badgeSize as keyof typeof styles],
        styles[badgeType as keyof typeof styles],
        styles[badgeColor as keyof typeof styles],
        {
          [styles.withIconStart]: !!icon && iconPosition === "start",
          [styles.withIconEnd]: !!icon && iconPosition === "end",
          [styles.iconOnly]: iconOnly,
        },
      )}
      data-badge-size={badgeSize}
      data-badge-type={badgeType}
      data-badge-color={badgeColor}
      ref={ref}
      {...props}
    >
      {iconPosition === "start" && iconBlock}
      {!iconOnly && children}
      {iconPosition === "end" && iconBlock}
    </div>
  );
}));